"use client";

import { useEffect, useState } from "react";

type MicReading = { level: number | null; silent: boolean };
const UNAVAILABLE: MicReading = { level: null, silent: false };

/** Optional local meter for an existing recording stream. Never captures or plays audio. */
export function useRecordingMicLevel(getStream?: () => MediaStream | null) {
  const [reading, setReading] = useState<MicReading>(UNAVAILABLE);

  useEffect(() => {
    if (!getStream) return;
    let context: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let analyser: AnalyserNode | null = null;
    let samples: Uint8Array<ArrayBuffer> | null = null;
    let timer: ReturnType<typeof setInterval> | undefined;
    let quietSince: number | null = null;
    let smoothed = 0;

    const release = () => {
      source?.disconnect();
      analyser?.disconnect();
      if (context) void context.close().catch(() => undefined);
      context = null;
      source = null;
      analyser = null;
      samples = null;
      quietSince = null;
      smoothed = 0;
      // The recorder owns the stream and its tracks, not this optional meter.
    };

    const sample = () => {
      try {
        if (!context || context.state !== "running" || !analyser || !samples) {
          quietSince = null;
          setReading(UNAVAILABLE);
          return;
        }
        const track = getStream()?.getAudioTracks()[0];
        if (!track || track.readyState !== "live") {
          clearInterval(timer);
          release();
          setReading(UNAVAILABLE);
          return;
        }
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (const value of samples) sum += ((value - 128) / 128) ** 2;
        const rms = Math.sqrt(sum / samples.length);
        const target =
          track.enabled && !track.muted ? Math.min(1, Math.max(0, (rms - 0.012) / 0.16)) : 0;
        const now = Date.now();
        if (target >= 0.04) quietSince = null;
        else quietSince ??= now;
        smoothed += (target - smoothed) * (target > smoothed ? 0.45 : 0.2);
        setReading({
          level: Math.round(smoothed * 100) / 100,
          silent: quietSince !== null && now - quietSince >= 5_000,
        });
      } catch {
        clearInterval(timer);
        release();
        setReading(UNAVAILABLE);
      }
    };

    const syncVisibility = () => {
      clearInterval(timer);
      release();
      setReading(UNAVAILABLE);
      if (document.hidden) return;
      const stream = getStream();
      const Context =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!stream || !Context) return;
      try {
        context = new Context();
        source = context.createMediaStreamSource(stream);
        analyser = context.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        samples = new Uint8Array(analyser.fftSize);
        if (context.state === "suspended") void context.resume().catch(() => undefined);
        timer = setInterval(sample, 100);
        sample();
      } catch {
        release();
      }
    };

    syncVisibility();
    document.addEventListener("visibilitychange", syncVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", syncVisibility);
      release();
    };
  }, [getStream]);

  return reading;
}
